import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { loginSuccess, setSelectedEvent } from "../../redux/slices/authSlice";
import { setAuthToken } from "../../utils/authToken";
import {
  loginApi,
  forgotPasswordApi,
  resetPasswordApi,
  FRONTEND_URL,
} from "../../server/Api";
import right_img from "../../assets/images/right_img.svg";
import login_logo from "../../assets/images/login_logo.png";
import "../../assets/css/login.css";
import toast from "react-hot-toast";

const roleLabel = (event) => {
  if (event?.isAdmin) return "Event Admin";
  return event?.role || "Team member";
};

const eventInitial = (name) =>
  String(name || "E")
    .trim()
    .charAt(0)
    .toUpperCase() || "E";

const Login = () => {
  const [email, setEmail] = useState("ashelar@tecogis.com");
  const [password, setPassword] = useState("Pass@123");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [rememberMe, setRememberMe] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventOptions, setEventOptions] = useState([]);
  const [pendingEventId, setPendingEventId] = useState(null);
  const [eventSearch, setEventSearch] = useState("");

  const sortedEventOptions = useMemo(() => {
    const q = eventSearch.trim().toLowerCase();
    return [...eventOptions]
      .filter((event) => {
        if (!q) return true;
        return (
          String(event.eventName || "")
            .toLowerCase()
            .includes(q) ||
          String(event.hostedBy || "")
            .toLowerCase()
            .includes(q) ||
          String(event.eventType || "")
            .toLowerCase()
            .includes(q)
        );
      })
      .sort((a, b) => {
        if (Boolean(a.isEventActive) !== Boolean(b.isEventActive)) {
          return a.isEventActive ? -1 : 1;
        }
        return String(a.eventName || "").localeCompare(
          String(b.eventName || "")
        );
      });
  }, [eventOptions, eventSearch]);

  const activeEventCount = useMemo(
    () => eventOptions.filter((e) => e.isEventActive).length,
    [eventOptions]
  );

  const handleContinueWithEvent = () => {
    const selected = eventOptions.find(
      (event) => String(event.eventId) === String(pendingEventId)
    );
    if (!selected || !selected.isEventActive) {
      toast.error("Please select an active event to continue.");
      return;
    }
    dispatch(setSelectedEvent(selected));
    setShowEventModal(false);
    navigate("/dashboard", { replace: true });
  };

  useEffect(() => {
    if (!showEventModal) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowEventModal(false);
        setPendingEventId(null);
        setEventSearch("");
        return;
      }
      if (e.key !== "Enter" || !pendingEventId) return;
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      e.preventDefault();
      const selected = eventOptions.find(
        (event) => String(event.eventId) === String(pendingEventId)
      );
      if (selected?.isEventActive) {
        dispatch(setSelectedEvent(selected));
        setShowEventModal(false);
        navigate("/dashboard", { replace: true });
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showEventModal, pendingEventId, eventOptions, dispatch, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (rememberMe) {
      localStorage.setItem("email", email);
    } else {
      localStorage.removeItem("email");
    }

    try {
      const response = await axios.post(
        loginApi(),
        { email: email.trim().toLowerCase(), password },
        { headers: { "Content-Type": "application/json" } }
      );

      setAuthToken(response.data.token);

      toast.success("Login Successful");

      const { user, events } = response.data;
      const eventList = events || [];
      const { isSuperAdmin } = user;

      dispatch(loginSuccess({ user, events: eventList }));

      if (isSuperAdmin) {
        navigate("/super-admin-dashboard", { replace: true });
      } else if (eventList.length === 1) {
        dispatch(setSelectedEvent(eventList[0]));
        navigate("/dashboard", { replace: true });
      } else {
        setEventOptions(eventList);
        setEventSearch("");
        const firstActive = eventList.find((ev) => ev.isEventActive);
        setPendingEventId(firstActive?.eventId || null);
        setShowEventModal(true);
      }
    } catch (error) {
      const apiMessage = error.response?.data?.message || error.response?.data;
      toast.error(
        typeof apiMessage === "string" && apiMessage
          ? apiMessage
          : "Unable to log in. Check that the backend is running."
      );
    }
  };

  const handleCloseEventModal = () => {
    setShowEventModal(false);
    setPendingEventId(null);
    setEventSearch("");
  };

  const handleForgotPassword = async () => {
    try {
      const response = await axios.post(
        forgotPasswordApi(),
        { email: resetEmail },
        { headers: { "Content-Type": "application/json" } }
      );
      alert(response.data.message);
      setShowForgotPassword(false);
    } catch (error) {
      alert(error.response?.data?.error || "Failed to send reset email.");
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await axios.post(
        resetPasswordApi(),
        { token: resetToken, password: newPassword },
        { headers: { "Content-Type": "application/json" } }
      );
      alert(response.data.message);
      setShowResetPassword(false);
    } catch (error) {
      alert(error.response?.data?.error || "Failed to reset password.");
    }
  };

  return (
    <>
      <div className="login-page">
        <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
          <div className="row shadow-lg rounded-4 overflow-hidden w-100 maxwidth1000">
            <div className="col-md-5 login-left p-5">
              <div className="text-center mb-5">
                <img
                  src={login_logo}
                  alt="Festiva Logo"
                  className="mb-3 login-logo"
                />
              </div>

              <form onSubmit={handleLogin}>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label fw-semibold">
                    Admin Login Id
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter Email"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="password" className="form-label fw-semibold">
                    Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="********"
                    required
                  />
                </div>

                <a
                  className="btn-link p-0 forgot-link"
                  onClick={() => setShowForgotPassword(true)}
                  style={{ fontSize: "0.875rem" }}
                >
                  Forgot Password?
                </a>
                <button
                  type="submit"
                  className="btn btn-success login-btn w-100 my-4"
                >
                  Sign In
                </button>
              </form>
            </div>

            <div className="col-md-7 login-right d-none d-md-flex bg-white align-items-center justify-content-center p-4">
              <img
                src={right_img}
                alt="Festiva illustration"
                className="login-hero-image"
              />
            </div>
          </div>
        </div>
      </div>

      {showForgotPassword && (
        <div className="modal show d-block">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Forgot Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowForgotPassword(false)}
                ></button>
              </div>
              <div className="modal-body">
                <label htmlFor="resetEmail">Enter your email:</label>
                <input
                  type="email"
                  className="form-control"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary teal-btn"
                  onClick={handleForgotPassword}
                >
                  Submit
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showResetPassword && (
        <div className="modal show d-block">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reset Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowResetPassword(false)}
                ></button>
              </div>
              <div className="modal-body">
                <label htmlFor="resetToken">Enter reset token:</label>
                <input
                  type="text"
                  className="form-control"
                  id="resetToken"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                />
                <label htmlFor="newPassword">Enter new password:</label>
                <input
                  type="password"
                  className="form-control"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleResetPassword}
                >
                  Reset Password
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowResetPassword(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEventModal && (
        <div
          className="login-event-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-event-modal-title"
        >
          <div className="login-event-modal__panel">
            <div className="login-event-modal__header">
              <p className="login-event-modal__eyebrow">Almost there</p>
              <h2
                id="login-event-modal-title"
                className="login-event-modal__title"
              >
                Choose your event
              </h2>
              <p className="login-event-modal__subtitle">
                Your login is linked to {eventOptions.length} event
                {eventOptions.length === 1 ? "" : "s"}
                {activeEventCount < eventOptions.length
                  ? ` · ${activeEventCount} active`
                  : ""}
                . Select where you want to continue.
              </p>
            </div>

            {eventOptions.length > 4 && (
              <div className="login-event-modal__search">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search by event name…"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  aria-label="Search events"
                />
              </div>
            )}

            <div className="login-event-modal__list" role="listbox">
              {sortedEventOptions.length === 0 ? (
                <p className="login-event-modal__empty">
                  No events match your search.
                </p>
              ) : (
                sortedEventOptions.map((event) => {
                  const selected =
                    String(pendingEventId) === String(event.eventId);
                  const inactive = !event.isEventActive;
                  return (
                    <button
                      key={event.eventId}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      disabled={inactive}
                      className={`login-event-option${
                        selected ? " is-selected" : ""
                      }${inactive ? " is-inactive" : ""}`}
                      onClick={() => {
                        if (!inactive) setPendingEventId(event.eventId);
                      }}
                      onDoubleClick={() => {
                        if (inactive) return;
                        setPendingEventId(event.eventId);
                        dispatch(setSelectedEvent(event));
                        setShowEventModal(false);
                        navigate("/dashboard", { replace: true });
                      }}
                    >
                      <span className="login-event-option__avatar" aria-hidden>
                        {event.eventLogo ? (
                          <img
                            src={`${FRONTEND_URL}/${event.eventLogo}`}
                            alt=""
                          />
                        ) : (
                          <span>{eventInitial(event.eventName)}</span>
                        )}
                      </span>
                      <span className="login-event-option__body">
                        <span className="login-event-option__name">
                          {event.eventName || "Untitled event"}
                        </span>
                        <span className="login-event-option__meta">
                          {event.hostedBy ? (
                            <span>Hosted by {event.hostedBy}</span>
                          ) : null}
                          {event.hostedBy && event.eventType ? (
                            <span aria-hidden> · </span>
                          ) : null}
                          {event.eventType ? (
                            <span>{event.eventType}</span>
                          ) : null}
                          {!event.hostedBy && !event.eventType ? (
                            <span>Festiva workspace</span>
                          ) : null}
                        </span>
                        <span className="login-event-option__tags">
                          <span
                            className={`login-event-tag${
                              event.isAdmin ? " is-admin" : ""
                            }`}
                          >
                            {roleLabel(event)}
                          </span>
                          {inactive ? (
                            <span className="login-event-tag is-inactive-tag">
                              Inactive
                            </span>
                          ) : (
                            <span className="login-event-tag is-active-tag">
                              Active
                            </span>
                          )}
                        </span>
                      </span>
                      <span
                        className={`login-event-option__radio${
                          selected ? " is-checked" : ""
                        }`}
                        aria-hidden
                      />
                    </button>
                  );
                })
              )}
            </div>

            <div className="login-event-modal__footer">
              <button
                type="button"
                className="btn login-event-modal__cancel"
                onClick={handleCloseEventModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn login-event-modal__continue"
                disabled={!pendingEventId}
                onClick={handleContinueWithEvent}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
